import {
  BookingChargeType,
  ModelType,
  PaymentType,
  QueryBookingPaymentRequestsWhereColumn,
  QueryPaymentsWhereColumn,
  UpdateBookingInput,
} from "@/gql/graphql";
import { Button } from "@/components/catalyst/button";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingPaymentRequestCreateDialog } from "@/app/Bookings/Components/BookingPaymentRequestCreateDialog";
import { PaymentItem } from "@/app/Bookings/Components/PaymentItem";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { IllustrationNotebook } from "@/assets/Illustrations";
import { PaymentTable } from "@/app/Payments/Components/PaymentTable";
import { BookingPaymentCreateDialog } from "@/app/Payments/Components/BookingPaymentCreateDialog";
import { CreditCardIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";
import { BookingPaymentRequestTable } from "@/app/Bookings/Components/BookingPaymentRequestTable";
import { ShipIcon } from "lucide-react";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { BookingChargeForm } from "@/app/Bookings/Forms/BookingChargeForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateBookingMutation } from "@/app/Bookings/GraphQL/bookingMutations";
import { PaymentSummarySection } from "@/app/Bookings/Components/PaymentSummarySection";
import { lazy } from "react";

const TakePaymentDialog = lazy(
  () => import("@/app/Bookings/Components/Dialogs/TakePaymentDialog"),
);

export function BookingPaymentScreen() {
  const { open } = useDialog(BookingPaymentRequestCreateDialog);
  const { open: openPaymentDialog } = useDialog(BookingPaymentCreateDialog);
  const { open: openTakePaymentDialog } = useDialog(TakePaymentDialog);
  const record = useBookingRecord();

  return (
    <div className="space-y-12 py-12">
      <ChargesBlock />

      <RecordScreenCard
        title="Customer Payments"
        buttons={
          <Button
            LeadingIcon={CreditCardIcon}
            onClick={() => openTakePaymentDialog({ booking: record })}
          >
            Take payment
          </Button>
        }
      >
        {record.customerPayments.length > 0 ? (
          <div className="grid w-full gap-6 md:grid-cols-3">
            {record.customerPayments.map((payment) => {
              return <PaymentItem payment={payment} key={payment.id} />;
            })}
          </div>
        ) : (
          <EmptyState
            Icon={IllustrationNotebook}
            header="Payments"
            subheader="There are no customer payments for this booking"
          />
        )}
      </RecordScreenCard>

      <BookingPaymentRequestTable
        id={"booking-payment-requests"}
        rightButtons={
          <Button
            LeadingIcon={PlusIcon}
            onClick={() => {
              open({
                booking: record,
              });
            }}
          >
            Request payment
          </Button>
        }
        queryVariables={{
          where: {
            column: QueryBookingPaymentRequestsWhereColumn.BookingId,
            value: record.id,
          },
        }}
      />

      <RecordScreenCard>
        <PaymentTable
          title="Supplier payments"
          searchable={false}
          rightButtons={
            <div className="flex gap-2">
              <Button
                outline
                LeadingIcon={ShipIcon}
                onClick={() => {
                  openPaymentDialog({
                    booking: record,
                    initialValues: {
                      description: `Ferry for ${record.reference} - ${record.departureOffice.city.name} to ${record.deliveryOffice.city.name}`,
                    },
                  });
                }}
              >
                Add ferry payment
              </Button>
              <Button
                LeadingIcon={PlusIcon}
                onClick={() => {
                  openPaymentDialog({
                    booking: record,
                  });
                }}
              >
                Add payment
              </Button>
            </div>
          }
          id={"booking-supplier-payments"}
          queryVariables={{
            where: {
              AND: [
                {
                  column: QueryPaymentsWhereColumn.AttributedToType,
                  value: ModelType.Booking,
                },
                {
                  column: QueryPaymentsWhereColumn.AttributedToId,
                  value: record.id,
                },
                {
                  column: QueryPaymentsWhereColumn.Type,
                  value: PaymentType.SupplierPayment,
                },
              ],
            },
          }}
        />
      </RecordScreenCard>
    </div>
  );
}

function ChargesBlock() {
  const record = useBookingRecord();
  const { mutateAsync } = useGqlMutation(updateBookingMutation);

  // Sort charges by payment flow order: Pay Now items first, then Pay Later items
  const sortOrder = {
    [BookingChargeType.RelocationRate]: 1,
    [BookingChargeType.DailyRate]: 2,
    [BookingChargeType.BookingFee]: 3,
    [BookingChargeType.PlatformHeldDeposit]: 4,
    [BookingChargeType.SupplierRefundDeposit]: 5,
    [BookingChargeType.AdditionalCharge]: 6,
    [BookingChargeType.PurchasableExtra]: 7,
  };

  const sortedCharges = [...record.charges].sort((a, b) => {
    const orderA = sortOrder[a.type] || 999;
    const orderB = sortOrder[b.type] || 999;
    return orderA - orderB;
  });

  return (
    <RecordScreenFormCard<UpdateBookingInput>
      title="Charge Summary"
      view={<PaymentSummarySection booking={record} />}
      initialValues={{
        charges: {
          upsert: sortedCharges.map((charge) => ({
            id: charge.id,
            type: charge.type,
            quantity: charge.quantity,
            supplier_unit_price_collectable:
              charge.supplier_unit_price_collectable,
            platform_unit_price_collectable:
              charge.platform_unit_price_collectable,
          })),
          delete: [],
        },
      }}
      onSubmit={async (values) => {
        await mutateAsync({
          id: record.id,
          input: values,
        });
      }}
    >
      <BookingChargeForm bookingId={record.id} />
    </RecordScreenFormCard>
  );
}
